import { useQuery } from '@tanstack/react-query';
import { HealthcareEntitiesService, UserTypeEnum } from '../../../../../js/api';

export const ADMIN_ROLE = 'ADMIN';
export const REP_UPDATE_PERMISSION = 'update__healthcare_entities__healthcareentityrep';
export const USER_DELETE_PERMISSION = 'delete__healthcare_entities__healthcareentityrep';

type UserLike =
  | {
      user_type?: UserTypeEnum;
      permissions?: ReadonlyArray<string>;
    }
  | null
  | undefined;

// Per-role default permission groups, fetched from the backend so adding or
// removing groups for a role doesn't require a frontend code change. ADMIN is
// not in the response — the frontend resolves it to "all available groups".
export const useRoleDefaults = () =>
  useQuery({
    queryKey: ['rep-role-defaults'],
    queryFn: async () => {
      const data = await HealthcareEntitiesService.healthcareEntitiesRepRoleDefaultsRetrieve();
      return data.defaults;
    },
    staleTime: Infinity,
    cacheTime: 24 * 60 * 60 * 1000,
  });

export const getDefaultGroupsForRole = (
  role: string | undefined | null,
  availableGroupCodes: string[],
  defaultsMap: Record<string, string[]> | undefined
): string[] => {
  if (!role) return [];
  if (role === ADMIN_ROLE) return availableGroupCodes;
  const defaults = defaultsMap?.[role] ?? [];
  const available = new Set(availableGroupCodes);
  return defaults.filter((code) => available.has(code));
};

// Matches AuthService.can_override_rep_groups on the backend: the rep update
// code, which staff hold through their portal_* groups. Hook-based checks
// (useCurrentUser, useHasPermission) can't be used here because this helper
// is invoked inside React-Hook-Form effects.
export const canOverrideRepGroups = (user: UserLike): boolean => {
  if (!user) return false;
  return user.permissions?.includes(REP_UPDATE_PERMISSION) ?? false;
};

export const canDeleteUser = (user: UserLike): boolean => {
  if (!user) return false;
  return user.permissions?.includes(USER_DELETE_PERMISSION) ?? false;
};
